import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabaseClient';
import { SITE_LOGO_URL } from '../lib/brand';
import { MemberCard } from '../components/ui/Cards';
import SafeIcon from '../common/SafeIcon';
import SolDivider from '../components/ui/SolDivider';
import { motion } from 'framer-motion';

export default function Landing() {
  const { inviteCode } = useParams();
  const { members, CAPITULOS, trainings, isAuthenticated } = useApp();

  // Mock toggle for testing the default vs. invite state
  const [devInviteMode, setDevInviteMode] = useState(false);
  const [inviterFromUrl, setInviterFromUrl] = useState(null);
  const [publicMembers, setPublicMembers] = useState([]);
  const [publicTrainings, setPublicTrainings] = useState([]);

  useEffect(() => {
    if (isAuthenticated) return undefined;
    let cancelled = false;
    (async () => {
      const [membersRes, trainingsRes] = await Promise.all([
        supabase.rpc('get_public_directorio_preview'),
        supabase.rpc('get_public_trainings_preview'),
      ]);
      if (cancelled) return;
      if (membersRes.error) {
        console.error('get_public_directorio_preview failed:', membersRes.error);
      } else {
        setPublicMembers(membersRes.data || []);
      }
      if (trainingsRes.error) {
        console.error('get_public_trainings_preview failed:', trainingsRes.error);
      } else {
        setPublicTrainings(trainingsRes.data || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Take a few items for the preview sections
  const previewMembers = isAuthenticated
    ? members.filter((m) => m.status === 'approved').slice(0, 3)
    : publicMembers.map((m, idx) => ({
        id: `public-${idx}`,
        nombre: m.nombre,
        foto_url: m.foto_url,
        rubro: m.rubro,
        capituloName: m.capitulo,
      }));
  const previewTrainings = isAuthenticated
    ? trainings.filter((t) => t.status === 'approved').slice(0, 2)
    : publicTrainings;

  useEffect(() => {
    let cancelled = false;
    if (!inviteCode) {
      setInviterFromUrl(null);
      return undefined;
    }
    (async () => {
      const { data, error } = await supabase.rpc('lookup_invite_code', {
        p_code: inviteCode.trim(),
      });
      if (cancelled) return;
      if (error) {
        console.error('lookup_invite_code failed:', error);
        setInviterFromUrl(null);
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      setInviterFromUrl(row?.id ? row : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  // Determine if we are in invite mode based on URL param or Dev toggle
  const isInviteMode = !!inviterFromUrl || devInviteMode;
  const inviter = inviterFromUrl || { nombre: 'un miembro', invite_code: 'DEMO' };

  // Use the actual parameter or the fallback for the link
  const activeInviteCode = inviterFromUrl ? inviteCode : inviter.invite_code;

  return (
    <div className="relative min-h-screen bg-[#FAF9F6] flex flex-col font-sans">
      
      {/* Dev Toggle (For previewing states only) */}
      <button 
        onClick={() => setDevInviteMode(!devInviteMode)}
        className="fixed bottom-6 right-6 z-50 bg-[#1B2A4E] text-[#F6B40E] text-xs font-bold px-5 py-3 rounded-full shadow-2xl hover:scale-105 transition-all flex items-center gap-2 label-technical"
      >
        <SafeIcon name="RefreshCw" className="w-3.5 h-3.5" />
        Toggle Invite State
      </button>

      {/* Simple Header */}
      <header className="absolute top-0 w-full z-50 px-6 py-5 flex items-center justify-between max-w-7xl mx-auto left-0 right-0">
        <div className="flex items-center gap-3">
          <img
            src={SITE_LOGO_URL}
            alt="Argentina Emprende"
            className="w-10 h-10 rounded-xl object-cover shadow-lg shrink-0"
          />
          <div className="hidden sm:block">
            <span className="font-editorial font-bold text-lg text-[#1B2A4E] leading-none block">Argentina</span>
            <span className="label-technical text-[#74ACDF] text-[9px]">Emprende</span>
          </div>
        </div>
        {isAuthenticated ? (
          <Link 
            to="/" 
            className="font-bold text-[#1B2A4E] hover:text-[#74ACDF] bg-white/80 backdrop-blur-md px-6 py-2.5 rounded-xl shadow-sm transition-colors border border-white/50 text-sm"
          >
            Ir al Inicio
          </Link>
        ) : (
          <Link 
            to="/login" 
            className="font-bold text-[#1B2A4E] hover:text-[#74ACDF] bg-white/80 backdrop-blur-md px-6 py-2.5 rounded-xl shadow-sm transition-colors border border-white/50 text-sm"
          >
            Ingresar
          </Link>
        )}
      </header>

      {/* 1. Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-4 overflow-hidden min-h-[85vh] flex items-center justify-center">
        {/* Celeste Flag Wash Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#74ACDF]/30 via-[#74ACDF]/5 to-[#FAF9F6] z-0" />
        
        {/* Argentine Icons Pattern Wash */}
        <div 
          className="absolute inset-0 z-0 opacity-5 pointer-events-none mix-blend-multiply"
          style={{ backgroundImage: 'url(https://media.publit.io/file/Argentina-Emprende/argentina-icons.png)', backgroundSize: '300px', backgroundRepeat: 'repeat' }}
        />

        {/* Large Sol de Mayo Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] text-[#F6B40E] opacity-10 pointer-events-none z-0 animate-[spin_180s_linear_infinite]">
          <svg viewBox="0 0 200 200" fill="currentColor">
            <circle cx="100" cy="100" r="30" />
            {[...Array(16)].map((_, i) => (
              <path key={i} d="M96 5 L104 5 L100 65 Z" transform={`rotate(${i * 22.5} 100 100)`} />
            ))}
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-xl rounded-[40px] p-8 md:p-16 shadow-2xl border border-white text-center"
          >
            {isInviteMode && inviter ? (
              <div className="mb-8 flex flex-col items-center">
                <div className="relative mb-6">
                  <img src={inviter.foto_url} alt={inviter.nombre} className="w-24 h-24 rounded-[24px] object-cover border-4 border-white shadow-xl rotate-3" />
                  <div className="absolute -bottom-3 -right-3 bg-[#F6B40E] rounded-full p-2.5 shadow-md text-[#1B2A4E]">
                    <SafeIcon name="Star" className="w-4 h-4" />
                  </div>
                </div>
                <span className="label-technical text-[#74ACDF] mb-3">Invitación Especial</span>
                <h1 className="text-4xl md:text-5xl font-editorial font-bold text-[#1B2A4E] leading-tight mb-4">
                  {inviter.nombre} te invitó a sumarte a <br className="hidden md:block"/> 
                  <span className="text-[#74ACDF] italic">Argentina Emprende</span>
                </h1>
                <p className="text-lg text-stone-500 max-w-xl mx-auto">
                  Aceptá la invitación para acceder al directorio privado, recursos exclusivos y eventos en tu ciudad.
                </p>
                <div className="mt-8">
                  <Link 
                    to={isAuthenticated ? "/" : `/registro/${activeInviteCode}`}
                    className="inline-flex items-center justify-center gap-3 bg-[#1B2A4E] hover:bg-[#253969] text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1"
                  >
                    {isAuthenticated ? 'Ir al Inicio' : 'Sumate ahora'} <SafeIcon name="ArrowRight" className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            ) : (
              <div className="mb-8">
                <span className="label-technical text-[#F6B40E] mb-4 block text-sm drop-shadow-sm">Movimiento de Emprendedores Argentinos en USA</span>
                <h1 className="text-5xl md:text-7xl font-editorial font-bold text-[#1B2A4E] leading-tight mb-6">
                  Argentina <span className="text-[#74ACDF] italic">Emprende</span>
                </h1>
                <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed font-medium">
                  Conectá, aprendé y crecé junto a la comunidad de empresarios y profesionales argentinos más activa de Estados Unidos.
                </p>
                <div className="mt-8">
                  <Link 
                    to={isAuthenticated ? "/" : "/registro"}
                    className="inline-flex items-center justify-center gap-3 bg-[#1B2A4E] hover:bg-[#253969] text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1"
                  >
                    {isAuthenticated ? 'Ingresar a la Plataforma' : 'Sumate al Movimiento'} <SafeIcon name="ArrowRight" className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* 2. Explainer Section */}
      <section className="py-24 px-4 bg-[#FAF9F6] relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-editorial font-bold text-[#1B2A4E] mb-8">¿Qué es Argentina Emprende?</h2>
          <p className="text-lg md:text-xl text-stone-500 leading-relaxed font-medium">
            Somos una red colaborativa de argentinos que decidimos apostar por el crecimiento en Estados Unidos. Más que un grupo de networking, somos una plataforma para compartir recursos, generar alianzas estratégicas y acelerar el éxito de nuestros negocios en el exterior, manteniendo vivas nuestras raíces.
          </p>
        </div>
      </section>

      <SolDivider className="max-w-xl mx-auto opacity-50" />

      {/* 3. Community Glimpse */}
      <section className="py-24 px-4 max-w-6xl mx-auto w-full">
        <div className="text-center mb-16">
          <span className="label-technical text-[#74ACDF] mb-2 block">Directorio Privado</span>
          <h2 className="text-3xl md:text-4xl font-editorial font-bold text-[#1B2A4E]">Nuestra Comunidad</h2>
          <p className="text-stone-500 mt-4 max-w-lg mx-auto">Conocé a algunos de los fundadores y profesionales que ya son parte de la red.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {previewMembers.map((member, idx) => (
            <motion.div 
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <MemberCard
                member={member}
                capituloName={
                  member.capituloName
                  || CAPITULOS.find(c => c.id === member.capitulo_id)?.nombre
                }
              />
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Link to={isAuthenticated ? "/directorio" : "/login"} className="inline-flex items-center gap-2 text-[#1B2A4E] font-bold hover:text-[#74ACDF] transition-colors label-technical text-sm tracking-widest uppercase">
            <SafeIcon name="Users" className="w-4 h-4" />
            Ver Directorio Completo
          </Link>
        </div>
      </section>

      {/* 4. Content Glimpse */}
      <section className="py-24 px-4 bg-white border-y border-stone-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-10 mb-16">
            <div className="max-w-xl">
              <span className="label-technical text-[#F6B40E] mb-2 block">Academia y Recursos</span>
              <h2 className="text-3xl md:text-4xl font-editorial font-bold text-[#1B2A4E]">Aprender de los Mejores</h2>
              <p className="text-stone-500 mt-4 leading-relaxed">
                Accedé a capacitaciones exclusivas en formato corto creadas por miembros expertos en legal, finanzas, visas y marketing en USA.
              </p>
            </div>
            <Link to={isAuthenticated ? "/academia" : "/login"} className="hidden md:inline-flex items-center gap-2 text-[#74ACDF] font-bold hover:text-[#1B2A4E] transition-colors label-technical">
              Explorar Academia <SafeIcon name="ArrowRight" className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {previewTrainings.map((t, idx) => (
              <motion.div 
                key={t.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex gap-6 p-6 rounded-[32px] border border-stone-100 shadow-sm hover:shadow-xl transition-all duration-300 bg-[#FAF9F6] group"
              >
                <div className="w-32 h-32 shrink-0 rounded-[24px] overflow-hidden relative shadow-md">
                  <img src={t.embed_url} alt={t.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-[#1B2A4E]/30 flex items-center justify-center group-hover:bg-[#1B2A4E]/10 transition-colors">
                    <div className="w-10 h-10 bg-[#F6B40E] rounded-full flex items-center justify-center shadow-lg">
                      <SafeIcon name="Play" className="w-4 h-4 text-[#1B2A4E] ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="flex-1 py-1 flex flex-col justify-center">
                  <div className="label-technical text-[#74ACDF] mb-2">{t.categoria}</div>
                  <h3 className="font-editorial font-bold text-xl text-[#1B2A4E] leading-tight mb-3 group-hover:text-[#74ACDF] transition-colors">{t.titulo}</h3>
                  <div className="text-xs text-stone-400 font-bold tracking-wider uppercase">{t.duracion} • Exclusivo Miembros</div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to={isAuthenticated ? "/academia" : "/login"} className="inline-flex items-center gap-2 text-[#74ACDF] font-bold hover:text-[#1B2A4E] transition-colors label-technical">
              Explorar Academia <SafeIcon name="ArrowRight" className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Closing CTA Band */}
      <section className="py-24 px-4 max-w-5xl mx-auto w-full">
        <div className="p-12 md:p-16 bg-gradient-to-br from-[#74ACDF] to-[#1B2A4E] rounded-[40px] text-white text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48 blur-3xl" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-editorial font-bold mb-6">Empezá tu camino acompañado</h2>
            <p className="text-white/80 mb-10 max-w-xl mx-auto text-lg leading-relaxed">
              No tenés que construir tu negocio solo. Sumate a la comunidad y multiplicá tus oportunidades en Estados Unidos.
            </p>
            <Link 
              to={isAuthenticated ? "/" : "/registro"} 
              className="inline-block bg-[#F6B40E] hover:bg-white text-[#1B2A4E] font-bold py-5 px-12 rounded-2xl transition-all hover:scale-105 shadow-xl text-lg"
            >
              {isAuthenticated ? 'Ir al Inicio' : 'Ingresar a la Comunidad'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}